import { aUEC } from './money.ts';
import type { Account, People } from './schemas.ts';

// Copy-for-Discord text (plan 2A Assumption 17), after Sierra's genBank (line 2581).
// Accounts become Sierra's "bank partners"; times are Discord timestamps, so each
// reader sees their own time zone.

/** A Discord timestamp: `<t:seconds:style>`. */
export function discordTime(at: Date, style: 'f' | 'D' | 'R'): string {
  return `<t:${String(Math.floor(at.getTime() / 1000))}:${style}>`;
}

/** An open loan as the bank report lists it (Chunk 4). */
export interface ReportLoan {
  borrower: string;
  left: number;
  due: number;
  dueDate: Date | null;
  overdue: boolean;
}

const KIND_LABEL: Readonly<Record<Account['kind'], string>> = { primary: 'Primary', held: 'Held' };

/** The bank report: the pool, each active account with its holders, and outstanding loans. */
export function bankReport(input: { at: Date; accounts: readonly Account[]; people: People; loans?: readonly ReportLoan[] }): string {
  const total = input.accounts.reduce((sum, account) => sum + account.balance, 0);
  const lines = ['## ITC corp bank', `As of ${discordTime(input.at, 'f')}`, `**Bank pool: ${aUEC(total)}**`];
  if (input.accounts.length === 0) {
    lines.push('', 'No accounts set up.');
  } else {
    lines.push('', '__Accounts__');
    for (const account of input.accounts) {
      const holders = account.holders.map((id) => input.people[id] ?? 'someone').join(', ');
      lines.push(`• **${account.name}** · ${KIND_LABEL[account.kind]}${holders === '' ? '' : ` (${holders})`} — ${aUEC(account.balance)}`);
    }
  }
  const loans = [...(input.loans ?? [])];
  if (loans.length > 0) {
    lines.push('', `__Outstanding loans: ${aUEC(loans.reduce((sum, loan) => sum + loan.left, 0))}__`);
    loans.sort((a, b) => (a.dueDate?.getTime() ?? Infinity) - (b.dueDate?.getTime() ?? Infinity));
    for (const loan of loans) {
      lines.push(`• **${loan.borrower}** — ${aUEC(loan.left)} left of ${aUEC(loan.due)}${loan.dueDate === null ? '' : ` · due ${discordTime(loan.dueDate, 'D')}`}${loan.overdue ? ' · **OVERDUE**' : ''}`);
    }
  }
  return lines.join('\n');
}
